import { EditorStarterItem } from '../../items/item-type';
import { TrackType } from '../../state/types';
import { getMaxSafeDurationInFrames } from './playback-rate-collision-detection';

export const MIN_PLAYBACK_RATE = 0.25;
export const MAX_PLAYBACK_RATE = 5;

// 클립이 가질 수 있는 최소 길이(프레임).
// 가속 시 결과 길이가 0으로 떨어져 클립이 깨지는 것을 막는 하한이며,
// 동시에 각 클립의 최대 가속 배속을 결정한다.
// (레포 전반의 ad-hoc `Math.max(1, ...)` 관례와 동일한 1프레임)
const MIN_CLIP_FRAMES = 1;

export type ComputePlaybackRateChangeParams = {
  // 드래그 시작 시 고정한 원본 구간의 프레임 수(durationInFrames × playbackRate).
  // floor된 durationInFrames를 매 틱 되먹이면 per-clip 천장이 드리프트하므로
  // (같은 클립이 드래그 속도에 따라 3x·5x로 제각각), 고정값을 받아 드래그 속도(중간
  // 단계 수)와 무관한 결과를 보장한다.
  sourceFramesInSegment: number;
  currentDurationInFrames: number;
  currentPlaybackRate: number;
  requestedRate: number;
  assetDurationInSeconds: number;
  startFromInSeconds: number;
  fps: number;
  item: EditorStarterItem;
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
};

/**
 * video/audio 클립 하나에 대해 요청 배속을 그 클립이 표현 가능한 범위로 clamp하고
 * 그에 맞는 durationInFrames를 계산한다.
 *
 * - 재생 구간(in/out)은 유지하고 타임라인 길이만 환산한다.
 * - 가속(요청 > 현재): 결과 길이가 MIN_CLIP_FRAMES 미만이 되지 않도록 배속을
 *   per-clip 상한(= 원본 구간의 프레임 수)으로 clamp한다.
 *   → 길이가 다른 여러 클립에 같은 배속을 적용해도 짧은 클립만 자기 상한에서 멈춘다.
 * - 감속(요청 < 현재): 충돌 지점·에셋 길이까지 확장을 허용한다.
 */
export const computePlaybackRateChange = ({
  sourceFramesInSegment,
  currentDurationInFrames,
  currentPlaybackRate,
  requestedRate,
  assetDurationInSeconds,
  startFromInSeconds,
  fps,
  item,
  tracks,
  items,
}: ComputePlaybackRateChangeParams): {
  playbackRate: number;
  durationInFrames: number;
} => {
  // 원본 구간 프레임 수가 곧 최대 배속이 된다
  // (duration = floor(sourceFrames / rate) >= MIN_CLIP_FRAMES 이려면 rate <= sourceFrames / MIN_CLIP_FRAMES).
  const maxRateForMinFrames = sourceFramesInSegment / MIN_CLIP_FRAMES;

  const cappedRate = Math.min(
    Math.max(requestedRate, MIN_PLAYBACK_RATE),
    MAX_PLAYBACK_RATE,
    Math.max(maxRateForMinFrames, MIN_PLAYBACK_RATE),
  );

  // 배속 변화가 없으면 그대로 둔다. 동배속 재입력(슬라이더 commit / input blur 등)이
  // 감속 경로로 오진되어 인접 클립 충돌 검사로 길이가 줄어드는 것을 막는다.
  if (cappedRate === currentPlaybackRate) {
    return {
      playbackRate: cappedRate,
      durationInFrames: currentDurationInFrames,
    };
  }

  // 고정된 원본 구간을 새 배속으로 환산한 타임라인 길이 (in/out 유지)
  // duration × rate = sourceFrames 이므로 duration = sourceFrames / rate.
  const idealNewDuration = Math.floor(sourceFramesInSegment / cappedRate);

  // 에셋의 최대 지속 시간은 계속 준수 (잘린 부분이 다시 나타나지 않도록)
  const maxDurationBasedOnAsset = Math.floor(
    ((assetDurationInSeconds - startFromInSeconds) / cappedRate) * fps,
  );

  // 가속(더 빠르게)이면 현재 길이에 맞춰 제한, 감속(더 느리게)이면 충돌 지점까지 확장 허용
  const safeDuration =
    cappedRate > currentPlaybackRate
      ? Math.min(currentDurationInFrames, idealNewDuration)
      : getMaxSafeDurationInFrames({
          item,
          tracks,
          items,
          newDuration: idealNewDuration,
        });

  const finalDuration = Math.max(
    MIN_CLIP_FRAMES,
    Math.min(safeDuration, maxDurationBasedOnAsset),
  );

  return { playbackRate: cappedRate, durationInFrames: finalDuration };
};
